import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import GeoAuth from '../dataSources/geo/GeoAuth'
import jwtDecode, { JwtPayload } from 'jwt-decode'
import { DataAction, EmptyAction, MessageAction } from 'src/CustomTypes'

export const SET_TOKEN = 'AUTH_ACTION_SET_TOKEN'
export const SET_USER_CREDENTIALS = 'AUTH_ACTION_SET_USER_CREDENTIALS'
export const SET_TOKEN_EXPIRED = 'AUTH_ACTION_SET_TOKEN_EXPIRED'
export const LOGIN_STARTED = 'AUTH_ACTION_LOGIN_STARTED'
export const LOGIN_FAILURE = 'AUTH_ACTION_LOGIN_FAILURE'
export const LOGIN_SUCCESS = 'AUTH_ACTION_LOGIN_SUCCESS'
export const LOGOUT = 'AUTH_ACTION_LOGOUT'

export interface TokenBundle {
  token: string
  tokenExpirationTimestamp: number
}

export interface UserCredentials {
  username: string
  password: string
}

interface AuthActions {
  setToken: (token: string, tokenExpirationTimestamp: number) => DataAction<TokenBundle>
  setUserCredentials: (username: string, password: string) => DataAction<UserCredentials>
  setTokenExpired: () => EmptyAction
  loginStarted: () => EmptyAction
  loginFailure: (errorMessage: string) => MessageAction
  loginSuccess: () => EmptyAction
  logout: () => EmptyAction

  // Thunk actions.
  login: (credentials: {username: string, password: string} | {permaToken: string}) => ThunkAction<Promise<void>, {}, {}, AnyAction>
}

export const authActions: AuthActions = {
  setToken: (token: string, tokenExpirationTimestamp: number): DataAction<TokenBundle> => {
    return {
      type: SET_TOKEN,
      data: { token, tokenExpirationTimestamp }
    }
  },
  setUserCredentials: (username: string, password: string): DataAction<UserCredentials> => {
    return {
      type: SET_USER_CREDENTIALS,
      data: { username, password }
    }
  },
  setTokenExpired: (): EmptyAction => {
    return {
      type: SET_TOKEN_EXPIRED
    }
  },
  loginStarted: (): EmptyAction => {
    return {
      type: LOGIN_STARTED
    }
  },
  loginFailure: (errorMessage: string): MessageAction => {
    return {
      type: LOGIN_FAILURE,
      message: errorMessage
    }
  },
  loginSuccess: (): EmptyAction => {
    return {
      type: LOGIN_SUCCESS
    }
  },
  logout: (): EmptyAction => {
    return {
      type: LOGOUT
    }
  },
  login: (credentials: {username: string, password: string} | {permaToken: string}): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
      return await new Promise<void>(resolve => {
        dispatch(authActions.loginStarted())
        const usingPermaToken = 'permaToken' in credentials
        const authParams = usingPermaToken ? credentials : {...credentials, role: 'Undervisningsplatform'}
        const auth = new GeoAuth(authParams)
        auth.fetchToken()
          .then(token => {
            const parsedToken = jwtDecode<JwtPayload>(token)
            if (parsedToken.exp === undefined) {
              dispatch(authActions.loginFailure('Invalid expiration timestamp!'))
              return
            }

            if (usingPermaToken) {
              dispatch(authActions.setUserCredentials((parsedToken as any)["GAL.UserName"], 'using-accesstoken'))
            }
            else {
              dispatch(authActions.setUserCredentials(credentials.username, credentials.password))
            }
            dispatch(authActions.setToken(token, parsedToken.exp * 1000)) // multiply by 1000 to get milliseconds
            dispatch(authActions.loginSuccess())
          })
          .catch(err => {
            dispatch(authActions.loginFailure(err.toString()))
          })
      })
    }
  }
}
